import { DataSource } from '@angular/cdk/collections';
import { PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { EmailMessage, EmailService, ListEmailsRequestListEmailsFilters, PagedRequestOptions } from '@vendasta/email';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { Site, SiteService } from '../../core/site';

interface PageOptions {
  index: number;
  size: number;
}

const EMAIL_HMSI_APP_ID = 'ESP_WEBSITE_PRO_HMSI';
const EMAIL_APP_ID = 'ESP_WEBSITE_PRO';
const TRANSACTIONAL_EMAIL_APP_ID = 'ESP_WEBSITE_PRO_TRANSACTIONAL';

export class EmailDataSource extends DataSource<EmailMessage> {
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private emails$$: ReplaySubject<EmailMessage[]> = new ReplaySubject(1);
  private emailsCount$$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  private pageOptions$$: BehaviorSubject<PageOptions> = new BehaviorSubject({
    index: 0,
    size: 10,
  });
  private toFilter$$: BehaviorSubject<string> = new BehaviorSubject('');
  private fromFilter$$: BehaviorSubject<string> = new BehaviorSubject('');
  private startFilter$$: BehaviorSubject<Date> = new BehaviorSubject(null);
  private endFilter$$: BehaviorSubject<Date> = new BehaviorSubject(null);

  loading$: Observable<boolean> = this.loading$$.asObservable();
  emails$: Observable<EmailMessage[]> = this.emails$$.asObservable();
  emailsCount$: Observable<number> = this.emailsCount$$.asObservable();
  searchTerm$: Observable<string> = this.searchTerm$$.asObservable();
  pageSize$: Observable<number>;

  totalResults = 0;

  /**
   * Constructs and returns a ListEmailsRequestListEmailsFilters object that contains the given filters
   * @param to string - the recipient of the email
   * @param from string - the email sender
   * @param start Date - lower limit of the date range
   * @param end Date - upper limit of the date range
   * @param site Site - site object containing information about the site for which email filters are constructed
   * @return ListEmailsRequestListEmailsFilters
   */
  private static getListEmailsFilters(
    to: string,
    from: string,
    start: Date,
    end: Date,
    site: Site,
  ): ListEmailsRequestListEmailsFilters {
    const espIds = [EMAIL_APP_ID, TRANSACTIONAL_EMAIL_APP_ID];
    if (site.partnerId === 'HMSI') {
      espIds.push(EMAIL_HMSI_APP_ID);
    }
    return new ListEmailsRequestListEmailsFilters({
      to: to,
      from: from,
      dateRange: {
        start: start,
        end: end,
      },
      espIds: espIds,
      partnerId: site.partnerId,
      businessId: site.accountId,
    });
  }

  /**
   * Constructs and returns the string representation of the cursor used for pagination
   * @param pageIndex - number used for indicating which page to list emails for
   * @param pageSize - number used for indicating the page size i.e number of emails to return per page
   */
  private static getCursor(pageIndex: number, pageSize: number): string {
    const offset = pageSize * pageIndex;
    return window.btoa(offset.toString(10));
  }

  /**
   * Returns a PageRequestOptions that contains the cursor and page size for requesting emails
   * @param pagingOptions - PageOptions objects containing cursor and page size
   * @return PagedRequestOptions
   */
  private static getPagingRequestOptions(pagingOptions: PageOptions): PagedRequestOptions {
    return new PagedRequestOptions({
      cursor: EmailDataSource.getCursor(pagingOptions.index, pagingOptions.size),
      pageSize: pagingOptions.size,
    });
  }

  constructor(
    private _api: EmailService,
    private siteService: SiteService,
    private snowplow: ProductAnalyticsService,
    private snackBarService: SnackbarService,
    private translateService: TranslateService,
  ) {
    super();
    this.pageSize$ = this.pageOptions$$.asObservable().pipe(map((p) => p.size));
  }

  /**
   * Monitors the page option events, which are used for paginating over emails
   * @param event - PageEvent containing the fields for pagination
   */
  pageOptionsChanged(event: PageEvent): void {
    this.emails$$.next([]);
    this.totalResults = 0;
    this.emailsCount$$.next(this.totalResults);
    this.pageOptions$$.next({ index: event.pageIndex, size: event.pageSize });
  }

  /**
   * Updates the search term used for searching emails based on the subject
   * @param term - string to search for
   */
  updateSearchTerm(term: string): void {
    this.emails$$.next([]);
    this.totalResults = 0;
    this.emailsCount$$.next(this.totalResults);
    this.searchTerm$$.next(term);
  }

  /**
   * Takes in a field that was updated in the table form and updates the filters used for searching for emails
   * @param fields - a filter group containing values to update
   */
  updateFilter(fields: any): void {
    this.emails$$.next([]);
    this.totalResults = 0;
    this.emailsCount$$.next(this.totalResults);

    if (fields['to'] !== null) {
      this.toFilter$$.next(fields['to']);
    }

    if (fields['from'] !== null) {
      this.fromFilter$$.next(fields['from']);
    }

    if (fields['sentAfter'] !== null) {
      this.startFilter$$.next(new Date(fields['sentAfter']));
    }
    if (fields['sentBefore'] !== null) {
      this.endFilter$$.next(new Date(fields['sentBefore']));
    }
  }

  /**
   * Returns an observable stream as a data source for the emails that are rendered by the table
   * @return Observable<EmailMessage[]>
   */
  connect(): Observable<EmailMessage[]> {
    combineLatest([
      this.searchTerm$,
      this.pageOptions$$,
      this.toFilter$$.asObservable(),
      this.fromFilter$$.asObservable(),
      this.startFilter$$.asObservable(),
      this.endFilter$$.asObservable(),
      this.siteService.site$,
    ])
      .pipe(
        tap(() => this.loading$$.next(true)),
        debounceTime(500),
        switchMap(
          ([searchTerm, pageOptions, to, from, start, end, site]: [
            string,
            PageOptions,
            string,
            string,
            Date,
            Date,
            Site,
          ]) => {
            return this._api
              .listEmails(
                searchTerm as string,
                EmailDataSource.getListEmailsFilters(to, from, start, end, site),
                EmailDataSource.getPagingRequestOptions(pageOptions as PageOptions),
              )
              .pipe(
                catchError(() => {
                  this.snackBarService.openErrorSnack(this.translateService.instant('API_ERROR.UNKNOWN'));
                  return of(null);
                }),
              );
          },
        ),
      )
      .subscribe((r) => {
        if (r) {
          this.emails$$.next(r.emails);
          this.totalResults = r.pagingMetadata.totalResults ? r.pagingMetadata.totalResults : 0;
          this.emailsCount$$.next(this.totalResults);
        } else {
          this.emails$$.next([]);
        }
        this.loading$$.next(false);
      });
    return this.emails$;
  }

  disconnect(): void {
    return;
  }
}
